/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { InfoBG, InfoText } from 'ui/theme/Color';
import CustomDialog from 'components/modals/common/CustomDialog';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { CommonUtil } from 'utils/CommonUtil';
import {
  findDailyReportEquipmentWorkers,
  saveDailyReportEquipmentWorkers,
} from 'apis/setup/SetupDailyReport';
import { WorkEquipmentWorker } from 'models/setup/SetupDailyReport';
import { useMessageBar } from 'hooks/useMessageBar';

type Props = {
  open: boolean;
  close: () => void;
  isReadOnly?: boolean;
  rowData?: WorkEquipmentWorker[];
  condition: {
    wkId: string;
    tgtEqpId: string;
    elmPrjId?: string;
    excpId?: string;
    eqpId: string; // 타이틀 노출용
    eqpNm: string; // 타이틀 노출용
  };
  onConfirm?: (rowData: WorkEquipmentWorker[]) => void;
};

/**
 * @desc 작업일보 > 작업자 등록 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.01.12   jd             신규 생성
 * 2026.01.20   jshwang12      작업자 수 소수점 2자리까지 입력 가능 (관리자, 설비별 작업자 모두), 작업자 컬럼 변경
 * 2026.09.30   jshwang12      작업자 값이 있는 셀 색상 표시
 */
const SetupDailyReportWorkerPopup = ({
  open,
  close,
  isReadOnly = true,
  condition,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<WorkEquipmentWorker[]>(props?.rowData || []);

  useEffect(() => {
    if ((props?.rowData || []).length < 1) {
      handleSearch(condition);
    }
  }, []);

  const handleSave = () => {
    if (!isReadOnly) {
      props?.onConfirm?.(rowData);
    }
    close();
    /*saveDailyReportEquipmentWorkers(rowData).then((res) => {
      openMessageBar({
        type: res.successOrNot === 'Y' ? 'confirm' : 'error',
        content:
          res.successOrNot === 'Y'
            ? t('com.label.저장되었습니다.', '저장되었습니다.')
            : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
      });
      if (res.successOrNot === 'Y') {
        handleSearch(condition);
      }
    });*/
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.작업자 목록', '작업자 목록'),
      t('setup.label.작업자 목록', '작업자 목록')
    );
  };

  const handleSearch = (params) => {
    if (params?.wkId && params?.tgtEqpId) {
      findDailyReportEquipmentWorkers(params?.wkId, params?.tgtEqpId).then((result) =>
        setRowData(result)
      );
    }
  };

  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'prjSpDivsCdNm',
        header: String(t('setup.label.구분', '구분')),
        minWidth: 70,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'prjSpCdNm',
        header: String(t('setup.label.단계', '단계')),
        minWidth: 70,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'inptMnhr',
        header: String(t('setup.label.작업자', '작업자')),
        minWidth: 70,
        width: 90,
        align: 'center',
        isRequired: false,
        isReadOnly: isReadOnly,
        cellTemplate: (params) => (params.value || 0).toLocaleString(),
      },
      {
        binding: 'tgtEqpId',
        visible: false,
      },
      {
        binding: 'spTpCd',
        visible: false,
      },
      {
        binding: 'prjSpCd',
        visible: false,
      },
      {
        binding: 'elmPrjId',
        visible: false,
      },
      {
        binding: 'excpId',
        visible: false,
      },
    ],
    [isReadOnly]
  );

  const onFormatItem = useEvent((grid, e) => {
    if (e.panel.cellType !== CellType.Cell) return;
    const binding = e.panel.columns[e.col]?.binding;
    const item = e.panel.rows[e.row]?.dataItem;
    const isEditing = !!grid.editRange?.contains(e.row, e.col);
    const hasValue = !isEditing && binding === 'inptMnhr' && Number(item?.[binding] || 0) !== 0;
    e.cell.style.backgroundColor = hasValue ? InfoBG.ColorB : '';
    e.cell.style.color = hasValue ? InfoText.ColorB : '';
    e.cell.style.fontWeight = hasValue ? 'bold' : '';
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.formatItem.addHandler(onFormatItem);
  };

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 작업자
    if (binding === 'inptMnhr') {
      const newVal = Number(grid.activeEditor?.value);
      if (Number.isNaN(newVal)) {
        e.cancel = true;
      }
      if (newVal < 0) {
        e.cancel = true;
      }
    }
  });

  const onCellEditEnded = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 작업자
    if (binding === 'inptMnhr') {
      const data = grid.rows[e.row].dataItem;
      const newVal = data[binding];
      const val = newVal ? Math.trunc(Number(newVal) * 100) / 100 : '';
      data.inptMnhr = val;
    }
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleSave}
      disableRipple
    >
      {t('com.button.저장', '저장')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={t('setup.label.작업자 등록', '작업자 등록')}
      size={'md'}
      open={open}
      onClose={close}
      onCancel={isReadOnly ? undefined : close}
      buttons={isReadOnly ? undefined : dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>
            {condition?.eqpId} : {condition?.eqpNm}
          </h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={613}
        isSelector={false}
        allowDragging={false}
        initialized={onInitialized}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
      />
    </CustomDialog>
  );
};

export default SetupDailyReportWorkerPopup;
